import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

/**
 * Map a user role to its URL prefix segment.
 * siteadmin → 'admin', all others match the role name.
 */
export const getRolePrefix = (role) => {
  const map = {
    superadmin: 'superadmin',
    tenantadmin: 'tenantadmin',
    siteadmin: 'admin',
    admin: 'admin',
    doctor: 'doctor',
    surgeon: 'doctor',
    anesthetist: 'doctor',
    dentist: 'doctor',
    physiotherapist: 'doctor',
    nurse: 'nurse',
    headnurse: 'nurse',
    pharmacist: 'staff',
    labtechnician: 'staff',
    radiologist: 'staff',
    receptionist: 'staff',
    billingstaff: 'staff',
    medicalrecords: 'staff',
    staff: 'staff',
    hrmanager: 'admin',
    itadmin: 'admin',
    qualityofficer: 'admin',
    patient: 'patient',
  };
  return map[role?.toLowerCase()] || role?.toLowerCase() || '';
};

/**
 * Hook that returns a function to build role-prefixed paths.
 * Usage:  const rp = useRolePath();  rp('/patients') → '/doctor/patients'
 */
export const useRolePath = () => {
  const { user } = useAuth();
  const prefix = getRolePrefix(user?.role);

  return useCallback((path) => {
    if (!path) return `/${prefix}`;
    const clean = path.startsWith('/') ? path : `/${path}`;
    // Don't double-prefix
    if (clean.startsWith(`/${prefix}/`) || clean === `/${prefix}`) return clean;
    return `/${prefix}${clean}`;
  }, [prefix]);
};

/**
 * Drop-in replacement for useNavigate() that auto-prefixes role paths.
 * Public paths (/login, /register, /portal) are NOT prefixed.
 */
const PUBLIC_PREFIXES = ['/login', '/register', '/portal'];

export const useRoleNavigate = () => {
  const nav = useNavigate();
  const rp = useRolePath();

  return useCallback((to, options) => {
    // navigate(-1) / navigate(1) — history traversal
    if (typeof to === 'number') {
      nav(to);
      return;
    }
    // String paths
    if (typeof to === 'string') {
      if (PUBLIC_PREFIXES.some(p => to.startsWith(p))) {
        nav(to, options);
      } else {
        nav(rp(to), options);
      }
      return;
    }
    // Object form { pathname, search, hash }
    if (to && typeof to === 'object' && to.pathname) {
      const prefixed = PUBLIC_PREFIXES.some(p => to.pathname.startsWith(p))
        ? to.pathname
        : rp(to.pathname);
      nav({ ...to, pathname: prefixed }, options);
      return;
    }
    nav(to, options);
  }, [nav, rp]);
};
